import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { useContext } from 'react';
import { AppContext } from '../context/AppContext';

const AlarmOverlay: React.FC = () => {
    const { isAlarming, stopAlarm, notifications } = useNotifications();
    const { currentUser } = useAuth();
    const { setView } = useContext(AppContext);

    if (!isAlarming) return null;

    // Get the most recent unread notification that triggered the alarm
    const latestNotif = notifications.find(n => !n.read && (
        n.type === 'new_order' || 
        n.type === 'new_mission' || 
        (n as any).type === 'nudge' ||
        n.type === 'driver_assigned'
    ));

    const handleAction = () => {
        stopAlarm();
        if (latestNotif?.orderId) {
            if (currentUser?.role === 'restaurateur') {
                setView({ screen: 'adminOrders', params: { initialTab: 'pending' } });
            } else if (currentUser?.role === 'livreur') {
                setView({ screen: 'livreurHome' });
            }
        }
    };

    const isLivreur = currentUser?.role === 'livreur';

    return (
        <AnimatePresence>
            {isAlarming && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-red-600/95 backdrop-blur-xl p-6 text-center"
                >
                    <motion.div
                        animate={{ 
                            scale: [1, 1.1, 1],
                            rotate: [0, -5, 5, 0]
                        }}
                        transition={{ 
                            duration: 0.5, 
                            repeat: Infinity,
                            ease: "easeInOut" 
                        }}
                        className="w-32 h-32 bg-white rounded-full flex items-center justify-center mb-8 shadow-[0_0_50px_rgba(255,255,255,0.5)]"
                    >
                        <span className="text-6xl">{isLivreur ? '🛵' : '🔔'}</span>
                    </motion.div>

                    <h1 className="text-4xl font-black text-white mb-4 uppercase tracking-tighter">
                        {latestNotif?.title || (isLivreur ? 'Nouvelle Mission !' : 'Nouvelle Commande !')}
                    </h1>
                    
                    <p className="text-red-100 text-xl font-bold mb-12 max-w-xs">
                        {latestNotif?.message || "Une nouvelle demande attend votre attention immédiate."}
                    </p>

                    <div className="flex flex-col gap-4 w-full max-w-sm">
                        <button
                            onClick={handleAction}
                            className="w-full py-5 bg-white text-red-600 rounded-2xl font-black text-xl shadow-2xl active:scale-95 transition-all flex items-center justify-center gap-3"
                        >
                            <span>👀</span> Voir la demande
                        </button>
                        
                        <button
                            onClick={stopAlarm}
                            className="w-full py-4 border-2 border-white/30 text-white rounded-2xl font-bold text-lg active:scale-95 transition-all"
                        >
                            Couper le son
                        </button>
                    </div>

                    {/* Background Pulsing Circles */}
                    <div className="absolute inset-0 z-[-1] overflow-hidden pointer-events-none">
                        <motion.div 
                            animate={{ scale: [1, 2], opacity: [0.3, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-4 border-white rounded-full"
                        />
                        <motion.div 
                            animate={{ scale: [1, 1.5], opacity: [0.2, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border-4 border-white rounded-full"
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AlarmOverlay;
